import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import uniqBy from 'lodash/uniqBy';

import {
  ChartType,
  ChartType as CommonChartType,
  PropertyType,
  ResourceType,
  SPECIAL_EVENTS,
} from 'common/report/constants';
import {BookmarkOverrides} from 'common/report/util/bookmark-overrides';
import {
  getBookmarkParamsWithAdditionalFilters,
  shouldSyncDateAndRetentionUnits,
} from 'common/report/retention/util/bookmark-overrides';
import {serializedRelativeDatesToRollingFullLengthDateRange} from 'common/util/date-converters';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {BookmarkDateRange, DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Segfilter} from 'common/widgets/property-filter-menu/models/segfilter';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {getSegmentType, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {
  comparisonDateFromBookmarkFormat,
  comparisonDateToBookmarkFormat,
} from 'common/query-builder/query-block/time-comparison-menu/utils';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import {FiltersOperator, QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {
  BirthIntervalUnit,
  isRelativeRollingDateRange,
  isRetentionCurveBasedChart,
  isRetentionTrendBasedChart,
  NON_INTERVALIZED_VALUE,
  RetentionAlignmentType,
  RetentionBookmarkParams,
  RetentionChartType,
  RetentionType,
} from 'common/report/retention/util/types';
import {
  API_CHART_TYPE_TO_COMMON_CHART_TYPE,
  COMMON_CHART_TYPE_TO_API_CHART_TYPE,
} from 'common/report/retention/util/constants';
import {SortBy, SortConfig, SortOrder} from 'common/report/util/sorting/types';
import {CommonDataTableFields} from 'common/components/data-table/types';
import {getFirstSegment} from 'common/query-builder/query-block/segment-menu/types';
import {
  getDefaultSortConfig,
  getPrimaryValueField,
  updateSortingForNewNumSegments,
} from 'common/report/util/sorting/table-sorting';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {LiftType} from 'common/report/util/lift/types';
import {bookmarkGroupByToGroupBy, groupByToBookmarkGroupBy} from 'common/query-builder/query-block/segment-menu/util';
import {bookmarkFilterByCohortToCohortFilters} from 'common/query-builder/query-block/filter-menu/util';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {GroupSortableTrendFields} from 'common/components/data-table/trend-data/constants';
import {
  bookmarkEventToEvent,
  bookmarkEventToEventAttribution,
  cohortsToFilterByCohortAndGroupBy,
  convertOnStringToSegment,
  EventAttributionItem,
  eventToBookmarkEvent,
  filterToBookmarkFilter,
  getDataGroupName,
  getFlattenedSegmentType,
  getGroupByName,
  getSegmentName,
  groupByToCohortsOrOn,
  isSegmented,
  isSegmentedByCohort,
  onToGroupBy,
} from 'common/report/retention/util/format';
import {getQueryType, RetentionQueryType} from 'common/report/retention/util/request';
import {Segmentation} from 'common/types/reports/query';

// TODO: figure out if/how I want to use this.
export interface QueryInterface {
  retentionType: RetentionType;
  retentionAlignmentType: RetentionAlignmentType;
  bornEvent: string;
  bornFilters: RetentionBookmarkParams['born_filters'];
  bornFiltersOp: FiltersOperator;
  bucketSizes: number[];
  chartType: RetentionChartType;
  dataGroupId: string;
  dateRange: BookmarkDateRange;
  dateUnit: BirthIntervalUnit;
  retentionUnit?: TimeUnit;
  cumulative: boolean;
  unbounded: boolean;
  percentageView: boolean;
  event: string;
  expandedSegments: string[];
  filters: RetentionBookmarkParams['filters'];
  filtersOp: FiltersOperator;
  filterByCohort: RetentionBookmarkParams['filter_by_cohort'];
  filterByEvent: RetentionBookmarkParams['filter_by_event'];
  groupBy: RetentionBookmarkParams['group_by'];
  segmentationEvent: string;
  retentionBucket: number;
  selectedSegments: string[];
  sortColumn: string;
  sortDesc: boolean;
  sorting: SortConfig;
  querySampling: boolean;
  timeComparison: ComparisonDate;
  liftComparison: LiftComparison;
}

const DEFAULTS: QueryInterface = Object.freeze({
  bornEvent: null,
  event: null,
  dataGroupId: null,
  bornFilters: [],
  bornFiltersOp: FiltersOperator.And,
  bucketSizes: [],
  expandedSegments: [],
  filters: [],
  filtersOp: FiltersOperator.And,
  filterByCohort: {},
  filterByEvent: {},
  chartType: CommonChartType.RetentionTable,
  cumulative: true,
  dateUnit: TimeUnit.Day,
  retentionUnit: TimeUnit.Day,
  dateRange: {type: DateRangeType.RelativeAfter, from_date: {unit: TimeUnit.Day, value: 7}, to_date: `$now`},
  groupBy: [],
  percentageView: true,
  querySampling: false,
  retentionType: `birth`,
  retentionAlignmentType: RetentionAlignmentType.Birth,
  segmentationEvent: null,
  selectedSegments: null,
  sortColumn: `cohort`,
  sortDesc: true,
  sorting: null,
  retentionBucket: 0,
  unbounded: true,
  timeComparison: null,
  liftComparison: null,
});

export const RETENTION_DEFAULT_EVENT = {
  filters: [],
  filtersOperator: FiltersOperator.And,
  event: {
    label: `Select Event`,
    value: null,
  },
  type: ResourceType.Event,
};

const UI_ONLY_FIELDS = [
  `expanded_segments`,
  `selected_segments`,
  `sort_desc`,
  `sort_column`,
  `sorting`,
  `percentage_view`,
  `chart_type`,
  `retention_bucket`,
] as const;
// asserts that each of UI_ONLY_FIELDS are assignable to type `keyof RetentionBookmarkParams` while
// preserving the `as const` nature of the original declaration. exported to avoid
// the "unused variable" tsc error.
export const _assertUiOnlyFieldsValid: ReadonlyArray<keyof RetentionBookmarkParams> = UI_ONLY_FIELDS;

export const ORDERED_TIME_UNITS: TimeUnit[] = [TimeUnit.Day, TimeUnit.Week, TimeUnit.Month];

/**
 * RetentionQuery represents the UI state, the information necessary to make a query,
 * and the logic to translate from URLs and any version of bookmark into the current UI
 * state.
 */
export class RetentionQuery implements QueryInterface {
  public readonly retentionType: RetentionType;
  public readonly retentionAlignmentType: RetentionAlignmentType;
  public readonly bornEvent: string;
  public readonly bornFilters: Segfilter[] | Segmentation[];
  public readonly bornFiltersOp: FiltersOperator;
  public readonly bucketSizes: number[];
  public readonly chartType: RetentionChartType;
  public readonly dataGroupId: string;
  /** TODO: make dateRange an actual DateRange object */
  public readonly dateRange: BookmarkDateRange;
  public readonly dateUnit: BirthIntervalUnit;
  public readonly retentionUnit: TimeUnit;
  public readonly cumulative: boolean;
  public readonly unbounded: boolean;
  public readonly percentageView: boolean;
  public readonly event: string;
  public readonly filters: Segfilter[] | Segmentation[];
  public readonly filtersOp: FiltersOperator;
  public readonly filterByCohort: RetentionBookmarkParams['filter_by_cohort'];
  public readonly filterByEvent: RetentionBookmarkParams['filter_by_event'];
  public readonly groupBy: Segment[];
  public readonly segmentationEvent: string;

  public readonly selectedSegments: string[];
  public readonly expandedSegments: string[];
  public readonly sorting: SortConfig;
  // TODO: Remove sortDesc and sortColumn once trend data table is GA
  public readonly sortDesc: boolean;
  public readonly sortColumn: string;
  public readonly retentionBucket: number;

  public readonly timeComparison: ComparisonDate;
  public readonly liftComparison: LiftComparison;

  public querySampling: boolean;

  constructor(initialParams: Partial<QueryInterface> = {}, updateParams: Partial<QueryInterface> = {}) {
    let defaults: Partial<QueryInterface> = DEFAULTS;
    if (initialParams.retentionType === `addiction`) {
      // The value of retentionUnit from DEFAULTS doesn't apply to addiction since addiction doesn't use retentionUnit
      defaults = {...defaults, retentionUnit: null};
    }

    const groupByChanged = `groupBy` in updateParams && initialParams.groupBy !== updateParams.groupBy;
    const currentLiftNeedsSegments =
      initialParams.liftComparison?.type === LiftType.Segment ||
      initialParams.liftComparison?.type === LiftType.Overall;
    if (groupByChanged && currentLiftNeedsSegments && !updateParams.liftComparison) {
      updateParams.liftComparison = null;
    }

    // Lift and time comparison doesn't work with Addiction yet, so we don't allow it and clear them if they are selected
    const changedToAddiction = `retentionType` in updateParams && updateParams.retentionType === `addiction`;
    if (changedToAddiction) {
      updateParams.liftComparison = null;
      updateParams.timeComparison = null;
    }

    Object.assign(
      this,
      defaults,
      pick(initialParams, Object.keys(DEFAULTS)),
      pick(updateParams, Object.keys(DEFAULTS)),
    );

    if (isEmpty(this.sorting)) {
      let sorting: SortConfig;
      // Initialize `sorting` if it's missing. Old bookmarks used `sortColumn`/`sortDesc` for storing sort
      // information which we convert to the new `sorting` format. Note, that only Retention curve used to have
      // have a sortable data table. Thus, `sortColumn`/`sortDesc` should only be converted to `sorting` for
      // Retention curve and not Retention trends. Even older bookmarks didn't store sort info at all, in which
      // case we use a default sort.
      if (this.chartType === CommonChartType.RetentionTable && !isNil(this.sortColumn) && !isNil(this.sortDesc)) {
        const sortOrder = this.sortDesc ? SortOrder.Descending : SortOrder.Ascending;
        sorting = getDefaultSortConfig({
          numSegmentColumns: this.groupBy.length || 1,
          valueSortField: `size`,
          flatSortConfig: {
            sortBy: this.sortColumn === `cohort` ? SortBy.Value : SortBy.Label,
            sortOrder,
          },
        });
      } else {
        sorting = this.getDefaultSorting();
      }
      this.sorting = sorting;
    }

    // @ts-ignore TS2704: The operand of a 'delete' operator cannot be a read-only property.
    delete this.sortColumn;
    // @ts-ignore TS2704: The operand of a 'delete' operator cannot be a read-only property.
    delete this.sortDesc;
  }

  getDefaultSorting({chartType = this.chartType}: {chartType?: RetentionChartType} = {}): SortConfig {
    const valueSortField = [CommonChartType.RetentionTrend, CommonChartType.RetentionTrendMetric].includes(chartType)
      ? CommonDataTableFields.CohortSize
      : // for Retention and Frequency Curve
        `size`;

    return getDefaultSortConfig({
      numSegmentColumns: this.groupBy.length || 1,
      valueSortField,
      allowGroupSort: true,
    });
  }

  createFromUpdate(params: Partial<QueryInterface>): RetentionQuery {
    return new RetentionQuery(this, params);
  }

  createFromChartTypeUpdate(chartType: RetentionChartType): RetentionQuery {
    const timeUnits: BirthIntervalUnit[] = ORDERED_TIME_UNITS;
    const params: Partial<QueryInterface> = {chartType};
    if (!this.isAddiction() && chartType === CommonChartType.RetentionTrend && !timeUnits.includes(this.dateUnit)) {
      // The `non_intervalized` unit isn't valid for trends view. If that's selected when switching to trends view,
      // we have to change the unit to something that's valid.
      params.dateUnit = ORDERED_TIME_UNITS[0];
    }

    params.sorting = this.getDefaultSorting({chartType});

    if (!this.isAddiction() && chartType !== CommonChartType.RetentionTrend) {
      // In non-trends chart type, we only have `retentionUnit` in the UI. But we still maintain separate `dateUnit`
      // and `retentionUnit` state (this will make it easier to have these separated in the UI in the future).
      // Consequently, we have to make change one of them to match the other. We copy retentionUnit -> dateUnit
      // (rather than the other way around) because retention unit exists for both chart types, so it makes to keep
      // that the same when switching chard types.
      params.dateUnit = this.retentionUnit;
    }

    return this.createFromUpdate(params);
  }

  createFromDateUnitUpdate(dateUnit: BirthIntervalUnit): RetentionQuery {
    return this.createFromUpdate({
      dateUnit,
    });
  }

  createFromDateRangeUpdate(dateRange: BookmarkDateRange): RetentionQuery {
    return this.createFromUpdate({
      dateRange,
    });
  }

  createFromRetentionTypeUpdate(retentionType: RetentionType): RetentionQuery {
    return this.createFromUpdate({
      retentionType,
      ...(retentionType === `addiction` ? {retentionUnit: null} : {retentionUnit: this.dateUnit as TimeUnit}),
    });
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  static createFromBookmark(params: Partial<RetentionBookmarkParams>): RetentionQuery {
    const parsed: Partial<QueryInterface> = {};
    if (params.date_range) {
      if (isRelativeRollingDateRange(params.date_range)) {
        parsed.dateRange = serializedRelativeDatesToRollingFullLengthDateRange({
          relativeFrom: Number(params.date_range.from),
          relativeTo: Number(params.date_range.to),
        });
      } else {
        parsed.dateRange = params.date_range;
      }
    }

    if (params.cohorts) {
      const parsedCohorts = cohortsToFilterByCohortAndGroupBy(params.cohorts, false);
      parsed.filterByCohort = parsedCohorts.filter_by_cohort;
      parsed.groupBy = parsedCohorts.group_by;
    } else {
      if (params.group_by) {
        parsed.groupBy = params.group_by;
      }

      if (params.filter_by_cohort) {
        parsed.filterByCohort = params.filter_by_cohort;
      }
    }

    if (params.filter_by_event) {
      parsed.filterByEvent = params.filter_by_event;
    }

    if (params.on) {
      parsed.groupBy = onToGroupBy(convertOnStringToSegment(params.on));
    }

    if (params.time_comparison) {
      parsed.timeComparison = comparisonDateFromBookmarkFormat(params.time_comparison);
    }

    // Validate that (1) segmentationEvent corresponds to one of the events
    // (2) that it's not garbage. null and [] exist in the persisted bookmarks.
    if (params.segmentation_event && [params.born_event, params.event].includes(params.segmentation_event)) {
      parsed.segmentationEvent = params.segmentation_event;
    }

    if (params.retention_type === `compounded`) {
      // compounded retention is removed in favor of first time retention. 2020-03-11
      copy(params.event, parsed, `bornEvent`);
      copy(params.filters, parsed, `bornFilters`);
      copy(`birth`, parsed, `retentionType`);
    } else if (params.retention_type === `birth`) {
      copy(params.born_event, parsed, `bornEvent`);
      copy(params.born_filters, parsed, `bornFilters`);
      copy(params.born_filters_op, parsed, `bornFiltersOp`);
      copy(params.retention_type, parsed, `retentionType`);
    } else {
      copy(params.retention_type, parsed, `retentionType`);
    }

    if (params.lift_comparison) {
      copy(params.lift_comparison, parsed, `liftComparison`);
    }

    // if unbounded retention is omit, default it to false
    if (params.retention_type === `compounded` || params.retention_type === `birth`) {
      parsed.unbounded = Boolean(params.unbounded_retention);
    }

    if (params.retention_type !== `addiction`) {
      if (params.retention_unit) {
        copy(params.retention_unit, parsed, `retentionUnit`);
        copy(params.bucket_sizes, parsed, `bucketSizes`);
      } else {
        // In the old format, `retention_unit` didn't exist on the bookmark. Convert bookmarks saved in the old
        // format to the new format where dateUnit and retentionUnit are split.
        copy(params.date_unit as TimeUnit, parsed, `retentionUnit`);
      }
    }

    parsed.chartType = API_CHART_TYPE_TO_COMMON_CHART_TYPE[params.chart_type];

    // No data translations to manage for these attributes.
    copy(params.event, parsed, `event`);
    copy(params.filters, parsed, `filters`);
    copy(params.filters_op, parsed, `filtersOp`);
    copy(params.date_unit, parsed, `dateUnit`);
    copy(params.percentage_view, parsed, `percentageView`);
    copy(params.selected_segments, parsed, `selectedSegments`);
    copy(params.expanded_segments, parsed, `expandedSegments`);
    copy(params.sort_column, parsed, `sortColumn`);
    copy(params.sort_desc, parsed, `sortDesc`);
    copy(params.sorting, parsed, `sorting`);
    copy(params.cumulative, parsed, `cumulative`);
    copy(params.retention_bucket, parsed, `retentionBucket`);
    copy(params.data_group_id, parsed, `dataGroupId`);
    copy(params.retention_alignment_type, parsed, `retentionAlignmentType`);
    // Omit null, undefined and empty string so that the defaults can be filled in.
    return new RetentionQuery(omitNullAndUndefinedAndEmptyString(parsed));
  }

  /**
   * Construct a serializable object which can reproduce the query and UI state.
   *
   * @param {boolean} [forQuery = false] if true, omit UI only fields
   * @param {boolean} [forUI = false] used for UI
   */
  generateBookmark({forQuery = false}: {forQuery?: boolean} = {}): RetentionBookmarkParams {
    /* eslint-disable camelcase */
    let bookmark: RetentionBookmarkParams = {
      date_unit: this.dateUnit,
      ...(!this.isAddiction()
        ? {
            retention_unit: this.retentionUnit,
            bucket_sizes: this.bucketSizes,
          }
        : {}),
      date_range: this.dateRange,
      chart_type: COMMON_CHART_TYPE_TO_API_CHART_TYPE[this.chartType],
      event: this.event,
      filters: this.filters,
      filters_op: this.filtersOp,
      born_event: this.bornEvent,
      born_filters: this.bornFilters,
      born_filters_op: this.bornFiltersOp,
      retention_type: this.retentionType,
      retention_alignment_type: this.retentionAlignmentType,
      filter_by_cohort: this.filterByCohort,
      filter_by_event: this.filterByEvent,
      segmentation_event: this.segmentationEvent, // omit if empty?
      group_by: this.groupBy,
      data_group_id: this.dataGroupId,
      percentage_view: this.percentageView,
      cumulative: this.cumulative,
      unbounded_retention: this.unbounded,
      selected_segments: this.selectedSegments,
      expanded_segments: this.getExpandedSegments(),
      sorting: this.sorting,
      retention_bucket: this.retentionBucket,
      ...(this.liftComparison ? {lift_comparison: this.liftComparison} : {}),
      ...(this.timeComparison ? {time_comparison: comparisonDateToBookmarkFormat(this.timeComparison)} : {}),
    };
    /* eslint-enable camelcase */

    if (this.retentionType !== `birth`) {
      bookmark = omit(bookmark, [`born_event`, `born_filters`]);
    }

    if (forQuery) {
      bookmark = omit(bookmark, UI_ONLY_FIELDS);
    }

    return bookmark;
  }

  getQueryType(): RetentionQueryType {
    return getQueryType(this.chartType);
  }

  /** Test query equality, ignoring UI-only parameters */
  queryEquals(other: RetentionQuery): boolean {
    return (
      this.getQueryType() === other.getQueryType() &&
      this.querySampling === other.querySampling &&
      isEqual(this.generateBookmark({forQuery: true}), other.generateBookmark({forQuery: true}))
    );
  }

  /** Test equality of UI state and bookmark, for persistence */
  equals(other: RetentionQuery): boolean {
    return this.getQueryType() === other.getQueryType() && isEqual(this.generateBookmark(), other.generateBookmark());
  }

  // Begin convenience methods for the view

  shouldSyncDateAndRetentionUnits() {
    return shouldSyncDateAndRetentionUnits(this.isAddiction(), this.chartType);
  }

  isNonIntervalized() {
    return this.dateUnit === NON_INTERVALIZED_VALUE;
  }

  getCohorts(dedupe = true) {
    const {cohorts} = groupByToCohortsOrOn(this.groupBy);

    if (dedupe) {
      return uniqBy(cohorts, (c) => c.id);
    }
    return cohorts;
  }

  isAddiction() {
    return this.retentionType === `addiction`;
  }

  /**
   * Checks if the group by is on a user property or cohorts.
   */
  isGroupByUser() {
    const group = this.groupBy ? this.groupBy[0] : null;
    return group && [ResourceType.Cohort, ResourceType.User].includes(getSegmentType(group));
  }

  isRetentionTrendBasedChart() {
    return isRetentionTrendBasedChart(this.chartType);
  }

  isRetentionCurveBasedChart() {
    return isRetentionCurveBasedChart(this.chartType);
  }

  isSegmented() {
    return isSegmented(this.groupBy);
  }

  isSegmentedByCohort() {
    return isSegmentedByCohort(this.groupBy);
  }

  getGroupByName(group: Segment): string {
    return getGroupByName(group);
  }

  getSegmentName(): string {
    return getSegmentName(this.groupBy);
  }

  getFlattenedSegmentType(): PropertyType {
    return getFlattenedSegmentType(this.groupBy);
  }

  isSegmentedByDataGroup(): boolean {
    // TODO - value comparisons: Consider changing if we support unlike prop comparisons b/w data group/nondata group props
    return this.groupBy.some((group) => {
      return getFirstSegment(group).dataGroupId !== undefined && getFirstSegment(group).dataGroupId !== null;
    });
  }

  getDataGroupName({plural}: {plural?: boolean} = {}): string {
    return getDataGroupName(this.dataGroupId, plural);
  }

  getSelectedSegments(): string[] {
    return this.selectedSegments ?? [];
  }

  getExpandedSegments(): string[] {
    return this.expandedSegments;
  }

  getCountingMethod(): string {
    return this.retentionType === `addiction`
      ? this.cumulative
        ? `cumulative`
        : `non-cumulative`
      : this.unbounded
      ? `unbounded`
      : `bounded`;
  }

  applyOverrides(overrides: BookmarkOverrides): RetentionQuery {
    if (isEmpty(overrides)) {
      return this;
    }

    const {additionalFilters, dateRangeOverride, timeUnitOverride, chartTypeOverride} = overrides;
    const dateRange = dateRangeOverride ? {dateRange: new DateRange(dateRangeOverride).getBookmarkFormat()} : {};
    const dateUnit = timeUnitOverride ? {dateUnit: timeUnitOverride} : {};
    const chartType = (chartTypeOverride ?? this.chartType) as RetentionChartType;

    const retentionUnit =
      shouldSyncDateAndRetentionUnits(this.isAddiction(), chartType) && timeUnitOverride
        ? {retentionUnit: timeUnitOverride}
        : {};

    const additionalPropertyFilters = additionalFilters?.map((f) => new PropertyFilter(f));

    const bookmarkParamsWithAdditionalFilters = getBookmarkParamsWithAdditionalFilters(
      this.generateBookmark(),
      additionalPropertyFilters,
    );

    return new RetentionQuery(this, {
      ...{filterByCohort: bookmarkParamsWithAdditionalFilters.filter_by_cohort},
      ...{filterByEvent: bookmarkParamsWithAdditionalFilters.filter_by_event},
      ...dateRange,
      ...dateUnit,
      ...retentionUnit,
      chartType,
    });
  }
}

function copy<T, K extends keyof T>(value: T[K], dest: T, destName: K) {
  if (typeof value === `boolean` || value) {
    dest[destName] = value;
  }
}

// eslint-disable-next-line @typescript-eslint/ban-types
function omitNullAndUndefinedAndEmptyString<T extends object>(obj: T): Partial<T> {
  return omitBy(obj, function filterEmptyValues(value, _key) {
    return value === undefined || value === null || ((value as unknown) as string) === ``;
  });
}

export function isQueryEmpty(params: RetentionBookmarkParams) {
  if (params.retention_type === `birth`) {
    return !params.event || !params.born_event;
  }
  return !params.event;
}

export function nullIfEmpty(x: string): string | null {
  return x === `` ? null : x;
}

export function getBookmarkUpdatesFromEntries(
  entries: QueryEventEntry[],
  query: RetentionQuery,
): Partial<RetentionQuery> {
  const updates: Partial<RetentionQuery> = {};
  let event, filters, filtersOp, bornEvent, bornFilters, bornFiltersOp;
  if (query.retentionType === `birth`) {
    const eventEntries = entries.map((entry) => eventToBookmarkEvent(entry));
    if (eventEntries.length !== 2) {
      // When a custom event is deleted, 1 fewer entry is returned.
      // Match the remaining event and filter to the proper entry in the query.
      const entry = eventEntries[0];
      if (entry.event === query.event && isEqual(query.filters, entry.filters)) {
        ({event, filters, filtersOp} = entry);
      } else {
        ({event: bornEvent, filters: bornFilters, filtersOp: bornFiltersOp} = entry);
      }
    } else {
      [{event: bornEvent, filters: bornFilters, filtersOp: bornFiltersOp}, {event, filters, filtersOp}] = eventEntries;
    }

    Object.assign(updates, {
      bornEvent,
      bornFilters,
      bornFiltersOp,
      event,
      filters,
      filtersOp,
    });
  } else {
    if (entries.length === 0) {
      // When a custom event is deleted, 1 fewer entry is returned.
      [event, filters] = [null, null];
    } else {
      ({event, filters, filtersOp} = eventToBookmarkEvent(entries[0]));
    }
    Object.assign(updates, {event, filters, filtersOp});
  }

  return updates;
}

export function getBookmarkUpdatesFromGroupBy(
  groupBy: Segment[],
  query: RetentionQuery,
  attributionSelection?: string | number,
): Partial<RetentionQuery> {
  const updates: Partial<QueryInterface> = {
    groupBy: groupByToBookmarkGroupBy(groupBy),
    segmentationEvent: groupBy.length === 0 ? null : nullIfEmpty(attributionSelection?.toString()),
  };

  if (query.groupBy.length !== groupBy.length) {
    updates.sorting = updateSortingOnGroupByChange(query.createFromUpdate(updates));
  }

  if (updates.groupBy.length && !isEqual(updates.groupBy, query.groupBy)) {
    // always default to sort in descending order by cohort size
    Object.assign(updates, {
      sortDesc: true,
      sortColumn: `cohort`,
    });
  }

  return updates;
}

export function updateSortingOnGroupByChange(query: RetentionQuery): SortConfig {
  let updatedSorting;
  const {sorting, chartType} = query;
  const numSegmentColumns = query.groupBy.length;
  const oldNumSegmentColumns = sorting.colSortAttrs.length;

  const valueField = getPrimaryValueField(sorting);
  // Curve (retention-table) group sortable fields: 'size' aka Total Profiles or any integer (bucket fields)
  const curveAndGroupSortable =
    chartType === ChartType.RetentionTable && (valueField === `size` || /^[0-9]+$/.test(valueField));
  const trendAndGroupSortable = chartType === ChartType.RetentionTrend && GroupSortableTrendFields.includes(valueField);
  // group sortable fields: CommonDataTableFields.CohortSize and CommonDataTableFields.Total ("totalValue")
  const trendMetricAndGroupSortable =
    chartType === ChartType.RetentionTrendMetric &&
    [CommonDataTableFields.CohortSize, CommonDataTableFields.Total].includes(valueField);
  const valueFieldIsGroupSortable = curveAndGroupSortable || trendAndGroupSortable || trendMetricAndGroupSortable;

  if (numSegmentColumns > 0) {
    updatedSorting = updateSortingForNewNumSegments({
      numSegmentColumns,
      oldNumSegmentColumns,
      valueFieldIsGroupSortable,
      sorting,
    });
  } else {
    updatedSorting = query.getDefaultSorting();
  }
  return updatedSorting;
}

export function getBookmarkUpdatesFromFilters(updatedFilters) {
  const {filterByCohort, filterByEvent} = filterToBookmarkFilter(updatedFilters as PropertyFilter[]);

  return {
    filterByCohort,
    filterByEvent,
  };
}

export function getQBEntries(query: RetentionQuery, customEvents: CustomEventDefinition[]): QueryEventEntry[] {
  const {
    bornEvent: queryBornEvent,
    bornFilters,
    bornFiltersOp,
    event: queryEvent,
    filters: queryFilters,
    filtersOp,
    retentionType,
  } = query;

  return retentionType === `birth`
    ? [
        bookmarkEventToEvent(queryBornEvent, bornFilters, bornFiltersOp, customEvents),
        bookmarkEventToEvent(queryEvent, queryFilters, filtersOp, customEvents),
      ]
    : [bookmarkEventToEvent(queryEvent, queryFilters, filtersOp, customEvents)];
}

export function getQBGroupBy(groupBy): Segment[] {
  return bookmarkGroupByToGroupBy(groupBy || []);
}

export function getQBFilters(filterByEvent, filterByCohort): PropertyFilter[] {
  return [
    ...(isEmpty(filterByEvent)
      ? []
      : (filterByEvent[`children`] as any[]).map((item) =>
          ResourceType.User in item ? item[ResourceType.User] : item[ResourceType.Event],
        )),
    ...(filterByCohort ? bookmarkFilterByCohortToCohortFilters(JSON.stringify(filterByCohort)) : []),
  ];
}

/** Get valid options for breakdown attribution. */
export function getBreakdownAttribution(
  query: RetentionQuery,
  customEvents: CustomEventDefinition[],
): EventAttributionItem[] {
  let breakdownAttribution: EventAttributionItem[] = [];
  if (!query.isGroupByUser() && query.retentionType === `birth`) {
    breakdownAttribution = bookmarkEventToEventAttribution(
      [query.bornEvent || SPECIAL_EVENTS.ANY_EVENT, query.event || SPECIAL_EVENTS.ANY_EVENT],
      customEvents,
    );
  }
  return breakdownAttribution;
}

/**
 * Compute a value for breakdown attribution.
 *
 * If there's an existing value, validate it. If invalid, return the first valid
 */
export function getAttributionSelection(
  query: RetentionQuery,
  customEvents: CustomEventDefinition[],
): number | string | null {
  const attributionSelection = query.segmentationEvent;
  const breakdownAttribution = getBreakdownAttribution(query, customEvents);
  if (breakdownAttribution.length > 0) {
    if (breakdownAttribution.filter((item) => item.value === attributionSelection).length > 0) {
      return attributionSelection;
    } else {
      // by default retention attribution will use query.event for attribution
      // when we can't match saved bookmark to any of the valid options, should always default to the 2nd valid option
      // there's one edge case for `breakdownAttribute` to only have length of 1, which is when bornEvent is same as event
      // in that case we default to 1st valid option
      return breakdownAttribution[1]?.value || breakdownAttribution[0].value;
    }
  }

  return null;
}
